/* CÉDA cart drawer
 * - Reserveringstimer: start bij het eerste product, loopt door als er meer
 *   wordt toegevoegd, en leegt de winkelwagen zodra hij op 0:00 springt.
 * - Verzendbalk: schuift naar de nieuwe stand en "plopt" bij gratis verzending.
 * - Verzendbescherming: schakelaar die het beschermingsproduct toevoegt/verwijdert. */
(() => {
  if (window.customElements.get('ceda-cart-timer')) return;

  const root = () => (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

  const store = {
    get(key) { try { return localStorage.getItem(key); } catch (e) { return null; } },
    set(key, value) { try { localStorage.setItem(key, String(value)); } catch (e) {} },
    remove(key) { try { localStorage.removeItem(key); } catch (e) {} },
  };

  const session = {
    get(key) { try { return sessionStorage.getItem(key); } catch (e) { return null; } },
    set(key, value) { try { sessionStorage.setItem(key, String(value)); } catch (e) {} },
  };

  const refreshCart = () => document.dispatchEvent(new CustomEvent('cart:refresh'));

  /* ---------- Reserveringstimer ---------- */

  const END_KEY = 'ceda_cart_timer_end';
  const COUNT_KEY = 'ceda_cart_timer_count';

  const resetTimer = () => {
    store.remove(END_KEY);
    store.set(COUNT_KEY, 0);
  };

  // Winkelwagen leeg (via verwijderen of na afloop) = volgende keer een nieuwe timer
  document.addEventListener('cart:change', (event) => {
    const cart = event.detail && event.detail.cart;
    if (cart && cart.item_count === 0) resetTimer();
  });

  let clearing = false;

  const clearCart = async () => {
    if (clearing) return;
    clearing = true;
    resetTimer();
    try {
      await fetch(`${root()}cart/clear.js`, { method: 'POST', headers: { 'X-Requested-With': 'XMLHttpRequest' } });
    } finally {
      clearing = false;
      refreshCart();
    }
  };

  class CedaCartTimer extends HTMLElement {
    connectedCallback() {
      const minutes = parseInt(this.getAttribute('minutes'), 10) || 10;
      const itemCount = parseInt(this.getAttribute('item-count'), 10) || 0;
      const previousCount = parseInt(store.get(COUNT_KEY), 10) || 0;
      const now = Date.now();

      this.end = parseInt(store.get(END_KEY), 10) || 0;

      // Alleen een nieuwe timer als er nog geen actieve is (lege winkelwagen,
      // of de vorige liep af terwijl de bezoeker niet op de site was).
      if (!this.end || previousCount === 0 || this.end <= now) {
        this.end = now + minutes * 60000;
        store.set(END_KEY, this.end);
      }

      store.set(COUNT_KEY, itemCount);

      this.output = this.querySelector('[data-ceda-time]');
      this.tick();
      this.interval = setInterval(() => this.tick(), 1000);
    }

    disconnectedCallback() {
      clearInterval(this.interval);
    }

    tick() {
      const secondsLeft = Math.max(0, Math.round((this.end - Date.now()) / 1000));

      if (this.output) {
        this.output.textContent = `${Math.floor(secondsLeft / 60)}:${String(secondsLeft % 60).padStart(2, '0')}`;
      }

      if (secondsLeft === 0) {
        clearInterval(this.interval);
        this.hidden = true;
        clearCart();
      } else {
        this.hidden = false;
      }
    }
  }

  window.customElements.define('ceda-cart-timer', CedaCartTimer);

  /* ---------- Verzendbalk ---------- */

  const PROGRESS_KEY = 'ceda_ship_progress';

  class CedaShipBar extends HTMLElement {
    connectedCallback() {
      const fill = this.querySelector('.ceda-ship__fill');
      const bar = this.querySelector('.ceda-ship__bar');
      const target = parseFloat(this.getAttribute('progress')) || 0;
      const previous = parseFloat(session.get(PROGRESS_KEY));

      session.set(PROGRESS_KEY, target);

      if (!fill || !bar || isNaN(previous) || previous === target) return;

      const reachedNow = target >= 100 && previous < 100;

      if (reachedNow) bar.classList.remove('is-complete');

      fill.style.transition = 'none';
      fill.style.width = `${Math.min(previous, 100)}%`;
      fill.getBoundingClientRect();
      fill.style.transition = '';

      requestAnimationFrame(() => {
        fill.style.width = `${target}%`;
      });

      if (reachedNow) {
        this.classList.add('is-celebrating');
        setTimeout(() => bar.classList.add('is-complete'), 500);
        setTimeout(() => this.classList.remove('is-celebrating'), 1400);
      }
    }
  }

  window.customElements.define('ceda-ship-bar', CedaShipBar);

  /* ---------- Verzendbescherming ---------- */

  class CedaProtection extends HTMLElement {
    connectedCallback() {
      this.toggle = this.querySelector('[role="switch"]');

      // Alleen de bescherming over in de winkelwagen? Dan weghalen.
      if (this.hasAttribute('orphan')) {
        resetTimer();
        this.update(false);
        return;
      }

      if (this.toggle) this.toggle.addEventListener('click', () => this.onClick());
    }

    onClick() {
      if (this.busy) return;
      const isOn = this.toggle.getAttribute('aria-checked') === 'true';
      this.toggle.setAttribute('aria-checked', String(!isOn));
      this.update(!isOn);
    }

    async update(turnOn) {
      this.busy = true;
      this.classList.add('is-busy');

      try {
        if (turnOn) {
          await fetch(`${root()}cart/add.js`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
            body: JSON.stringify({ items: [{ id: Number(this.getAttribute('variant-id')), quantity: 1 }] }),
          });
        } else if (this.getAttribute('line-key')) {
          await fetch(`${root()}cart/change.js`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
            body: JSON.stringify({ id: this.getAttribute('line-key'), quantity: 0 }),
          });
        }
      } finally {
        this.busy = false;
        refreshCart();
      }
    }
  }

  window.customElements.define('ceda-protection', CedaProtection);
})();
